<script setup lang="ts">
  import { italianDateFormat, useLocale } from '@tundr/i18n';
  import { disabledClass } from '@tundr/theme';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { VueDatePicker } from '@vuepic/vue-datepicker';

  type DatePickerInstance = {
    closeMenu: () => void;
    openMenu: () => void;
    selectDate: () => void;
  };
  import '@vuepic/vue-datepicker/dist/main.css';
  import { format } from 'date-fns';
  import { it } from 'date-fns/locale';
  import {
    computed,
    onMounted,
    onUnmounted,
    ref,
    useTemplateRef,
    watch,
  } from 'vue';
  import FontIcon from '../../FontIcon/FontIcon.vue';
  import { formatDateInput } from '../../utils/strings.utils';
  import { InputSizeVariant, InputWidthVariant } from '../InputWrapper';
  import { inputWidthClass } from '../InputWrapper/InputWrapper.css.ts';
  import { TextInput } from '../TextInput';
  import './DatePicker-override.css.ts';
  import {
    calendarIconClass,
    calendarIconSizeClass,
    customDatePickerMenuClass,
    datePickerBaseClass,
    datePickerInputClass,
  } from './DatePicker.css.ts';
  import type { DatePickerWithModelValue } from './DatePicker.types.ts';

  const props = withDefaults(defineProps<DatePickerWithModelValue>(), {
    teleport: false,
  });

  const { currentLanguageDateFormat } = useLocale();

  const emit = defineEmits<{
    'update:modelValue': [Date | undefined];
    blur: [Event | undefined];
  }>();

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const datepicker = useTemplateRef<DatePickerInstance>('datepicker');
  const debounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
  const inputValue = ref<string>('');

  // Sync inputValue with modelValue
  watch(
    () => props.modelValue,
    (newValue) => {
      if (!newValue) {
        inputValue.value = '';
        return;
      }

      inputValue.value = format(newValue, currentLanguageDateFormat.value, {
        locale: it,
      });
    },
    { immediate: true },
  );

  // Normalize to UTC midnight to avoid timezone issues
  const normalizeDate = (date: Date) =>
    new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
    );

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      emit('update:modelValue', undefined);
      return;
    }

    emit('update:modelValue', normalizeDate(date));
  };

  const handleInput = (date: string | undefined) => {
    if (debounceTimeout.value) clearTimeout(debounceTimeout.value);

    if (!date) {
      inputValue.value = '';
      emit('update:modelValue', undefined);
      return;
    }

    inputValue.value = date;

    debounceTimeout.value = setTimeout(() => {
      const parsedDate = formatDateInput(date);
      if (!parsedDate) return;

      // Normalize to UTC midnight to avoid timezone issues
      const normalizedDate = normalizeDate(parsedDate);

      inputValue.value = format(
        normalizedDate,
        currentLanguageDateFormat.value,
        {
          locale: it,
        },
      );
      datepicker.value!.closeMenu();
      handleDateSelect(normalizedDate);
    }, 500);
  };

  defineOptions({
    inheritAttrs: false,
  });

  const datePickerContainerClasses = computed<string[]>(() => [
    inputWidthClass[props.width || InputWidthVariant.AUTO],
  ]);

  const datePickerClasses = {
    [datePickerBaseClass]: true,
    [disabledClass]: props.disabled,
  };

  const onBlur = () => {
    emit('blur', undefined);
  };

  const onFocusOut = () => {
    emit('blur', undefined);
  };

  const calendarIconClasses = computed<string[]>(() => [
    calendarIconClass,
    calendarIconSizeClass[props.size ?? InputSizeVariant.DEFAULT],
  ]);

  onMounted(() => {
    if (props.autofocus && datepicker.value) {
      datepicker.value.openMenu();
    }
  });

  onUnmounted(() => {
    if (debounceTimeout.value) clearTimeout(debounceTimeout.value);
  });
</script>

<template>
  <div :class="datePickerContainerClasses">
    <VueDatePicker
      :classes="datePickerClasses"
      :id="id"
      ref="datepicker"
      :name="name"
      :locale="it"
      autocomplete="off"
      :format-locale="it"
      :min-date="minDate"
      :max-date="maxDate"
      :format="italianDateFormat"
      :prevent-min-max-navigation="preventMinMaxNavigation"
      :start-date="startDate"
      :focus-start-date="focusStartDate"
      :enable-time-picker="false"
      :disabled="disabled || loading || readonly"
      :model-value="modelValue"
      :menu-class-name="customDatePickerMenuClass"
      :clearable="false"
      :timezone="timeZone"
      :auto-apply="true"
      :no-swipe="true"
      text-input
      :autofocus="autofocus"
      :teleport="teleport"
      @update:model-value="handleDateSelect"
      @blur="onBlur"
      v-bind="$attrs"
    >
      <template #clear-icon><span /></template>
      <template #dp-input>
        <TextInput
          :class="[inputClass, datePickerInputClass]"
          :meta="meta"
          :size="size"
          :test-id="testId"
          :disabled="disabled || loading"
          :loading="loading"
          :readonly="readonly"
          :width="width"
          :id="`date-picker-input-${name}`"
          :name="name"
          :autofocus="autofocus"
          :label="label"
          :placeholder="placeholder"
          :error="error"
          @blur="onBlur"
          @focusout="onFocusOut"
          @update:model-value="handleInput"
          :model-value="inputValue"
        >
          <template #iconRight>
            <FontIcon
              :class="calendarIconClasses"
              :icon-name="TUNDR_ICONS.CALENDAR"
            />
          </template>
        </TextInput>
      </template>
    </VueDatePicker>
  </div>
</template>

<style>
  pre {
    width: 100%;
  }
</style>
